import { normalizeLanguageCode } from '../i18n/languages';
import { getLegalDocument } from './legalContent.js';
import { collectLegalSourceCoverage } from './legalSourceInventory.js';
import { LEGAL_PAGE_DEFINITIONS } from './legalPageManifest.js';
import { validateLegalConfig } from './legalValidation.js';

const legalContentModules = import.meta.glob('../content/legal/**/*.md', {
  query: '?raw',
  import: 'default',
});

const LEGAL_SOURCE_COVERAGE = collectLegalSourceCoverage({
  contentPaths: Object.keys(legalContentModules),
});

export const LEGAL_CONTENT_LOCALES = Object.freeze([...LEGAL_SOURCE_COVERAGE.supportedLocales]);
export const LEGAL_SHIPPED_LOCALES = Object.freeze([...LEGAL_SOURCE_COVERAGE.supportedLocales]);

const LEGAL_CONTENT_LOCALE_SET = new Set(LEGAL_SHIPPED_LOCALES);

function resolveLegalLocale(locale) {
  const normalized = normalizeLanguageCode(locale);
  if (LEGAL_CONTENT_LOCALE_SET.has(normalized)) {
    return normalized;
  }
  return 'en';
}

function resolveLegalContentLoader(file, locale) {
  const normalizedLocale = resolveLegalLocale(locale);
  const localizedPath = `../content/legal/${normalizedLocale}/${file}.md`;
  const englishPath = `../content/legal/en/${file}.md`;
  const legacyEnglishPath = `../content/legal/${file}.md`;

  return (
    legalContentModules[localizedPath] ||
    legalContentModules[englishPath] ||
    legalContentModules[legacyEnglishPath] ||
    null
  );
}

function createMarkdownLegalPage({ slug, path, titleKey, sourceFile, mode = 'all', tocMode = 'always' }) {
  return {
    kind: 'markdown',
    slug,
    path,
    titleKey,
    mode,
    tocMode,
    async loadContent(locale = 'en') {
      const loader = resolveLegalContentLoader(sourceFile, locale);
      if (!loader) {
        return '';
      }
      const markdown = await loader();
      return String(markdown || '');
    },
  };
}

function createStructuredLegalPage({ slug, path, titleKey, mode = 'all', tocMode = 'auto' }) {
  return {
    kind: 'structured',
    slug,
    path,
    titleKey,
    mode,
    tocMode,
    getDocument(locale = 'en') {
      return getLegalDocument(slug, locale);
    },
  };
}

function createLegalPage(entry) {
  if (entry.kind === 'structured') {
    return createStructuredLegalPage(entry);
  }

  return createMarkdownLegalPage(entry);
}

const allLegalPages = Object.freeze(
  LEGAL_PAGE_DEFINITIONS.map((entry) => createLegalPage(entry)),
);

validateLegalConfig();

export function getLegalPages({ isComingSoon = false } = {}) {
  if (!isComingSoon) {
    return allLegalPages;
  }

  return allLegalPages.filter((page) => page.mode !== 'full-only');
}

export const footerLegalLinks = Object.freeze([
  { labelKey: "legal.links.impressum", path: "/impressum" },
  { labelKey: "legal.links.privacy", path: "/privacy" },
]);
